import type { StudyGuideTutorBinding } from "../utils/study-guide-tutor-lifecycle";
import { unlockVoiceCallAudio } from "../utils/voice-call-audio";
import { create } from "zustand";
import {
  SidePanelTab,
  SidePanelLocation,
  ContentMediumType,
  CardData,
  ChatLocations,
  DeckObject,
} from "../constants/GlobalTypesAndEnumsAndConstants";
import { askQuestionsChatStore, cardStore, usePDFViewerStore, useReviewDeckStore } from "./globalStore";
import {
  getMediaSourceStartSecond,
  isSourceViewerSupported,
  resolveCardSourceContext,
} from "../backend/utils/card-source-context";

// ============================================================================
// PANEL NAVIGATION STORE
// ============================================================================

interface PanelNavigationState {
  // Core state
  isPanelOpen: boolean;
  activeTabId: SidePanelTab | null;

  // Determines which features/tabs are available based on where the panel is opened from
  // e.g., DECK_REVIEW shows chat + source viewer, LIVE_RECORDING shows transcript + chat
  sidePanelLocation: SidePanelLocation | null;

  availableTabsInPanel: SidePanelTab[];

  // Panel UI options
  showClosePanelButton: boolean;
  panelMaxHeight?: string; // CSS height value like '600px' or '80vh'

  // Actions
  openPanel: (sidePanelLocation: SidePanelLocation, initialTab?: SidePanelTab) => void;
  closePanel: () => void;
  switchTab: (tabId: SidePanelTab) => void;
  setAvailableTabsInPanel: (tabs: SidePanelTab[]) => void;
  setShowClosePanelButton: (show: boolean) => void;
  setPanelMaxHeight: (height?: string) => void;
}

export const usePanelNavigationStore = create<PanelNavigationState>((set, get) => ({
  isPanelOpen: false,
  activeTabId: null,
  sidePanelLocation: null,
  availableTabsInPanel: [],
  showClosePanelButton: true,
  panelMaxHeight: undefined,

  openPanel: (sidePanelLocation, initialTab) => {
    // Determine which tabs should be available based on where the panel is being opened from
    const tabs = getAvailableTabsForLocation(sidePanelLocation);
    set({
      isPanelOpen: true,
      sidePanelLocation: sidePanelLocation,
      availableTabsInPanel: tabs,
      activeTabId: initialTab || tabs[0] || null,
    });
  },

  closePanel: () => {
    set({
      isPanelOpen: false,
      activeTabId: null,
      sidePanelLocation: null,
      availableTabsInPanel: [],
    });
  },

  switchTab: (tabId) => {
    const { availableTabsInPanel } = get();
    if (availableTabsInPanel.includes(tabId)) {
      set({ activeTabId: tabId });

      // Tab switch handled by the new store only
    }
  },

  setAvailableTabsInPanel: (tabs) => set({ availableTabsInPanel: tabs }),
  setShowClosePanelButton: (show) => set({ showClosePanelButton: show }),
  setPanelMaxHeight: (height) => set({ panelMaxHeight: height }),
}));

// Helper function to determine which tabs are available based on where the panel is opened from
function getAvailableTabsForLocation(sidePanelLocation: SidePanelLocation): SidePanelTab[] {
  switch (sidePanelLocation) {
    case SidePanelLocation.DECK_REVIEW:
      // When reviewing cards/decks, show chat and source viewer tabs
      return [SidePanelTab.CHAT, SidePanelTab.SOURCE_CONTENT_VIEWER];
    case SidePanelLocation.LIVE_RECORDING:
      // When in live recording, show transcript and chat tabs
      return [SidePanelTab.LIVE_RECORDING_TRANSCRIPT, SidePanelTab.CHAT];
    default:
      return [SidePanelTab.CHAT];
  }
}

// ============================================================================
// CONTENT NAVIGATION STORE
// ============================================================================

interface ContentNavigationState {
  // PDF Navigation
  // The page number from the card's location_context (where the card content came from)
  cardPdfPageLocationContext: number | null;
  currentPdfPage: number;

  // Video Navigation
  // The timestamp in seconds from the card's location_context (where in the video the card came from)
  cardVideoLocationContextInSeconds: number | null;
  currentVideoPlaybackTimeInSeconds: number;

  // Webpage Navigation
  webpageUrl: string | null;

  // Actions
  navigateToPdfPage: (page: number) => void;
  // Clears the navigation target after the PDF viewer has navigated to the requested page
  clearPdfNavigation: () => void;

  navigateToVideoTimeInSeconds: (seconds: number) => void;
  // Clears the navigation target after the video player has seeked to the requested time
  clearVideoNavigation: () => void;

  navigateToWebpage: (url: string) => void;
  clearWebpageNavigation: () => void;

  clearAllNavigation: () => void;
}

export const useContentNavigationStore = create<ContentNavigationState>((set) => ({
  cardPdfPageLocationContext: null,
  currentPdfPage: 1,
  cardVideoLocationContextInSeconds: null,
  currentVideoPlaybackTimeInSeconds: 0,
  webpageUrl: null,

  navigateToPdfPage: (page) => set({ cardPdfPageLocationContext: page }),
  clearPdfNavigation: () => set({ cardPdfPageLocationContext: null }),

  navigateToVideoTimeInSeconds: (seconds) => set({ cardVideoLocationContextInSeconds: seconds }),
  clearVideoNavigation: () => set({ cardVideoLocationContextInSeconds: null }),

  navigateToWebpage: (url) => set({ webpageUrl: url }),
  clearWebpageNavigation: () => set({ webpageUrl: null }),

  clearAllNavigation: () =>
    set({
      cardPdfPageLocationContext: null,
      cardVideoLocationContextInSeconds: null,
      webpageUrl: null,
    }),
}));

// ============================================================================
// SIDE PANEL ORCHESTRATOR
// ============================================================================

export class SidePanelOrchestrator {
  /**
   * The question's source-location action opens View Source from a closed or
   * Chat panel, but acts as a close toggle when that review source is already
   * visible. Returns whether the action opened or switched to View Source.
   */
  static toggleSourcePanelForCardReview(options: {
    currentCardData?: CardData | null;
    chatLocationName: ChatLocations;
    showClosePanelButton?: boolean;
    isViewingChatForASpecificCard?: boolean;
    isReviewingRecentMistakes?: boolean;
  }): boolean {
    const { isPanelOpen, activeTabId, sidePanelLocation } = usePanelNavigationStore.getState();
    const isCurrentReviewSourcePanelOpen =
      isPanelOpen &&
      sidePanelLocation === SidePanelLocation.DECK_REVIEW &&
      activeTabId === SidePanelTab.SOURCE_CONTENT_VIEWER;

    if (isCurrentReviewSourcePanelOpen) {
      this.closePanel();
      return false;
    }

    this.openSidePanelForCardReview({
      ...options,
      initialTab: SidePanelTab.SOURCE_CONTENT_VIEWER,
    });
    return true;
  }

  /**
   * Opens the side panel for card/deck review with proper context setup
   */
  static openSidePanelForCardReview(options: {
    currentCardData?: CardData | null;
    initialTab?: SidePanelTab;
    chatLocationName: ChatLocations;
    showClosePanelButton?: boolean;
    isViewingChatForASpecificCard?: boolean;
    isReviewingRecentMistakes?: boolean;
    mostRecentUserAnswerForQuestion?: string | null;
    currentFreeResponseAnswerFeedbackObject?: any;
    initialUserMessageForChat?: string;
    autoStartVoiceCall?: boolean;
    isAutoLaunchedExplainVoiceCall?: boolean;
    pendingExplainMessageAfterVoiceExit?: string;
    panelMaxHeight?: string;
    shouldShowTabsInChatSidePanel?: boolean;
    // Summary outline surface only: the outline markdown the activating
    // surface is displaying (see ChatSidePanelProps.summaryOutlineContextText).
    summaryOutlineContextText?: string;
    summaryOutlineDeck?: DeckObject;
    studyGuideTutor?: StudyGuideTutorBinding;
  }) {
    const {
      currentCardData,
      initialTab,
      chatLocationName,
      showClosePanelButton = true,
      panelMaxHeight,
      isViewingChatForASpecificCard,
      isReviewingRecentMistakes,
      mostRecentUserAnswerForQuestion,
      currentFreeResponseAnswerFeedbackObject,
      initialUserMessageForChat,
      autoStartVoiceCall,
      isAutoLaunchedExplainVoiceCall,
      pendingExplainMessageAfterVoiceExit,
      shouldShowTabsInChatSidePanel = true,
      summaryOutlineContextText,
      summaryOutlineDeck,
      studyGuideTutor,
    } = options;

    // This runs inside the call/explain button's gesture, before mounting the
    // panel defers voice setup to an effect and loses Safari's activation.
    if (autoStartVoiceCall) unlockVoiceCallAudio();

    // Check if panel is already open
    const { isPanelOpen, activeTabId: currentActiveTab, sidePanelLocation } = usePanelNavigationStore.getState();

    // Determine available tabs based on card content
    const tabs = this.determineTabsForCard(currentCardData, summaryOutlineDeck);

    if (isPanelOpen && sidePanelLocation === SidePanelLocation.DECK_REVIEW && initialTab) {
      // Panel is already open for deck review: keep it open and just update context + tab.
      usePanelNavigationStore.getState().setAvailableTabsInPanel(tabs);

      // Update the chat store to show tabs if there are multiple
      askQuestionsChatStore
        .getState()
        .setShouldShowTabsInChatSidePanel(shouldShowTabsInChatSidePanel && tabs.length > 1);

      // Only switch when the requested tab differs from what's currently shown.
      if (currentActiveTab !== initialTab) {
        usePanelNavigationStore.getState().switchTab(initialTab);

        // Handle content navigation if switching to source tab
        if (initialTab === SidePanelTab.SOURCE_CONTENT_VIEWER) {
          this.navigateToCardSource(currentCardData, summaryOutlineDeck);
        }
      }

      // The panel was already open, so we won't rebuild chatSidePanelParameters
      // below. Merge the voice-call flags into the existing params so
      // ChatViewComponent still picks them up — including the explain-nudge
      // pending message + button flag, which otherwise would be lost on the
      // common desktop case where the panel is already open at the 3rd press.
      if (autoStartVoiceCall) {
        const existingChatSidePanelParameters = askQuestionsChatStore.getState().chatSidePanelParameters;
        if (existingChatSidePanelParameters) {
          askQuestionsChatStore.getState().setChatSidePanelParameters({
            ...existingChatSidePanelParameters,
            autoStartVoiceCall: true,
            isAutoLaunchedExplainVoiceCall,
            pendingExplainMessageAfterVoiceExit,
          });
        }
      }
      return;
    }

    // Set up navigation store
    usePanelNavigationStore.getState().openPanel(SidePanelLocation.DECK_REVIEW, initialTab || tabs[0]);
    usePanelNavigationStore.getState().setAvailableTabsInPanel(tabs);
    usePanelNavigationStore.getState().setShowClosePanelButton(showClosePanelButton);
    if (panelMaxHeight) {
      usePanelNavigationStore.getState().setPanelMaxHeight(panelMaxHeight);
    }

    // Set up chat context
    askQuestionsChatStore.getState().setChatLocation(SidePanelLocation.DECK_REVIEW);
    askQuestionsChatStore.getState().setChatSidePanelParameters({
      currentCardData,
      chatLocationName,
      showCloseButton: showClosePanelButton,
      isViewingChatForASpecificCard,
      isReviewingRecentMistakes,
      mostRecentUserAnswerForQuestion,
      currentFreeResponseAnswerFeedbackObject,
      initialUserMessageForChat,
      autoStartVoiceCall,
      isAutoLaunchedExplainVoiceCall,
      pendingExplainMessageAfterVoiceExit,
      shouldShowTabsInChatSidePanel: tabs.length > 1,
      maxHeight: panelMaxHeight,
      activeTabId: initialTab || null,
      summaryOutlineContextText,
      summaryOutlineDeck,
      studyGuideTutor,
    });

    // Set the old state for components not yet migrated
    askQuestionsChatStore.setState({
      isAskQuestionsChatModalOpened: true,
      isViewingChatForASpecificCard: isViewingChatForASpecificCard || false,
      shouldShowTabsInChatSidePanel: tabs.length > 1,
    });

    // Set the correct tab in the old state system
    if (initialTab === SidePanelTab.SOURCE_CONTENT_VIEWER) {
      this.navigateToCardSource(currentCardData, summaryOutlineDeck);
    }
  }

  /**
   * Opens the side panel for live recording with transcript and chat tabs
   */
  static openSidePanelForLiveRecording(recordingId: string) {
    // Set up navigation
    usePanelNavigationStore
      .getState()
      .openPanel(SidePanelLocation.LIVE_RECORDING, SidePanelTab.LIVE_RECORDING_TRANSCRIPT);

    // Set up chat context
    askQuestionsChatStore.getState().setChatLocation(SidePanelLocation.LIVE_RECORDING);
    askQuestionsChatStore.getState().setActiveRecordingId(recordingId);

    // Clear any review context
    askQuestionsChatStore.getState().clearChatForLocationSwitch();
  }

  /**
   * Handles when a user clicks a citation in a chat message
   * Navigates to the specific page/timestamp referenced in the citation
   */
  static handleChatMessageCitationClick(options: {
    contentMediumType: ContentMediumType;
    pageOrSecondsLocationContext: number; // Page number for PDFs, seconds for videos
    pageUrl?: string;
  }) {
    const { contentMediumType, pageOrSecondsLocationContext, pageUrl } = options;

    // Navigate to source content based on content type
    if (contentMediumType === ContentMediumType.PDF) {
      useContentNavigationStore.getState().navigateToPdfPage(pageOrSecondsLocationContext);
      usePDFViewerStore.getState().setPageToNavigateToInPDFViewer(pageOrSecondsLocationContext);
      usePanelNavigationStore.getState().switchTab(SidePanelTab.SOURCE_CONTENT_VIEWER);
    } else if (contentMediumType === ContentMediumType.YouTubeVideo) {
      const videoStartSecond = getMediaSourceStartSecond(pageOrSecondsLocationContext);
      useContentNavigationStore.getState().navigateToVideoTimeInSeconds(videoStartSecond);
      cardStore.getState().setSecondToNavigateToInYoutubeViewer(videoStartSecond);
      usePanelNavigationStore.getState().switchTab(SidePanelTab.SOURCE_CONTENT_VIEWER);
    } else if (contentMediumType === ContentMediumType.Webpage && pageUrl) {
      window.open(pageUrl, "_blank");
    }
  }

  /**
   * Closes the side panel and cleans up all related state
   */
  static closePanel() {
    // Close panel
    usePanelNavigationStore.getState().closePanel();

    // Clear navigation targets
    useContentNavigationStore.getState().clearAllNavigation();

    // Clear chat if in live recording context
    const sidePanelLocation = usePanelNavigationStore.getState().sidePanelLocation;
    if (sidePanelLocation === SidePanelLocation.LIVE_RECORDING) {
      askQuestionsChatStore.getState().clearChatForLocationSwitch();
    }

    // Clear old state
    askQuestionsChatStore.setState({
      isAskQuestionsChatModalOpened: false,
      chatSidePanelParameters: null,
      shouldShowTabsInChatSidePanel: false,
    });
  }

  /**
   * Switches between tabs in the side panel
   */
  static switchTab(tabId: SidePanelTab) {
    usePanelNavigationStore.getState().switchTab(tabId);

    // Tab switching handled by new store only
  }

  // Private helper methods

  private static determineTabsForCard(currentCardData: CardData | null, summaryOutlineDeck?: DeckObject): SidePanelTab[] {
    const tabs = [SidePanelTab.CHAT];

    const currentReviewDeck = summaryOutlineDeck ?? useReviewDeckStore.getState().currentReviewDeck;
    const sourceContext = resolveCardSourceContext({
      card: currentCardData,
      deck: currentReviewDeck,
    });
    if (isSourceViewerSupported(sourceContext)) {
      tabs.push(SidePanelTab.SOURCE_CONTENT_VIEWER);
    }

    return tabs;
  }

  private static navigateToCardSource(currentCardData: CardData | null, summaryOutlineDeck?: DeckObject) {
    if (!currentCardData) return;

    const sourceContext = resolveCardSourceContext({
      card: currentCardData,
      deck: summaryOutlineDeck ?? useReviewDeckStore.getState().currentReviewDeck,
    });
    if (sourceContext.contentMediumType === ContentMediumType.PDF && sourceContext.location !== null) {
      useContentNavigationStore.getState().navigateToPdfPage(sourceContext.location);
      usePDFViewerStore.getState().setPageToNavigateToInPDFViewer(sourceContext.location);
    } else if (sourceContext.contentMediumType === ContentMediumType.YouTubeVideo && sourceContext.location !== null) {
      const adjustedStartTime = getMediaSourceStartSecond(sourceContext.location);
      useContentNavigationStore.getState().navigateToVideoTimeInSeconds(adjustedStartTime);
      cardStore.getState().setSecondToNavigateToInYoutubeViewer(adjustedStartTime);
    }
  }
}
